/**
 * Action: Motion to quash a subpoena (civil case, San Francisco Superior Court).
 *
 * Every rule cited here comes from the files in data/. Anything the files do not
 * cover is listed under "verify" in the checklist rather than asserted.
 */
import {
  addCourtDays, addDays, fmt, fmtISO, HOLIDAY_DATA_THROUGH, isCourtDay, parse, prevCourtDay, todayISO,
} from "../lib/courtdays.js";

export const id = "motion-to-quash-subpoena";
// The statewide forms its checklist can link, which a case offers when this action is added to it.
export const forms = [];

/* ------------------------------------------------------------------ labels */
const ROLE_TXT = {
  witness: "You (or your organization) were served with the subpoena",
  consumer: "Someone else was served for records about you (a bank, medical provider, phone company, etc.)",
  employee: "Someone else was served for your employment records",
  speech: "Someone was served to identify you in a case about your online or free-speech activity",
  other: "You are affected by a deposition subpoena served on someone else, but you are not a consumer or employee whose records are sought",
};
const TYPE_TXT = {
  dep_records: "Deposition subpoena for business records only",
  dep_testimony: "Deposition subpoena for testimony only",
  dep_both: "Deposition subpoena for testimony and records",
  trial_attend: "Subpoena to attend a trial or hearing",
  trial_records: "Subpoena to bring records or things to a trial or hearing (subpoena duces tecum)",
  unsure: "Not sure",
};
const METHOD_TXT = {
  personal: "personal delivery",
  eservice: "electronic service",
  mail_ca: "mail within California",
  overnight: "overnight delivery, express mail or fax",
  mail_us: "mail within the U.S.",
};
const opts = (o) => Object.entries(o).map(([v, l]) => ({ v, l }));

const isTrialType = (a) => ["trial_attend", "trial_records"].includes(a.stype);
const hasRecords = (a) => ["dep_records", "dep_both", "trial_records", "unsure"].includes(a.stype);
const hasTestimony = (a) => ["dep_testimony", "dep_both", "unsure"].includes(a.stype);
const isDep = (a) => ["dep_records", "dep_testimony", "dep_both", "unsure"].includes(a.stype);

function routeOf(a) {
  if (a.role === "witness" || a.role === "speech") return "motion";
  if (a.role === "other") return "protective";
  return a.route === "motion" ? "motion" : "objection";
}
const deptOf = (a) => (a.parity === "odd" ? "301" : "302");
const skipIfObjection = (a) => a.role && routeOf(a) === "objection";

/* ------------------------------------------------------------------ grounds */
function timeShort(a) {
  if (a.stype !== "dep_records" || !a.comply) return false;
  let need = null;
  if (a.served) need = addDays(parse(a.served), 15);
  if (a.issued) {
    const i = addDays(parse(a.issued), 20);
    need = need && need > i ? need : i;
  }
  return !!(need && parse(a.comply) < need);
}

function groundsCatalog(a) {
  const list = [];
  const add = (gid, text, cite, suggested, note) => list.push({ id: gid, text, cite, suggested: !!suggested, note: note || "" });
  const rec = hasRecords(a);
  const tes = hasTestimony(a);
  if (a.role === "witness") {
    add("service", "The subpoena was not properly served", isDep(a) ? "CCP 2020.220(b)-(c)" : "CCP 1987(a)", a.servedHow === "other",
      isDep(a)
        ? "Deposition subpoenas are served by personal delivery on the deponent, or on an officer, director, custodian of records or authorized agent of an organization."
        : "A subpoena is served by delivering a copy to the witness personally.");
    add("time", "Not enough time to locate records or travel before the compliance date", "CCP 2020.220(a); 2020.410(c); 1985.3(d)", timeShort(a),
      "A records-only deposition subpoena must set compliance no earlier than 20 days after issuance or 15 days after service, whichever is later.");
    add("fee", "Witness fee or mileage was not paid or offered", "CCP 2020.230; 1987(a)", a.fee === "no",
      "For testimony subpoenas, the fee may be paid at service or when the deponent attends. For records-only subpoenas, the fee must accompany service. For trial subpoenas, it must be given or offered if demanded.");
  }
  if (tes && (a.role === "witness" || a.role === "other")) {
    add("place", "The deposition location is farther than the statute allows", "CCP 2025.250(a),(c)", a.distance === "far",
      a.entity === "org"
        ? "An organization that is not a party is deposed within 75 miles of its principal California office unless it consents."
        : "An individual is deposed within 75 miles of residence, or in the county where the case is pending and within 150 miles.");
  }
  if (rec) {
    add("particularity", "Records are not described with reasonable particularity (vague or overbroad)", "CCP 2020.410(a); 2020.510(a)(2)", false,
      "Items must be listed individually or as reasonably particularized categories.");
    add("consumerNotice", "Consumer or employee notice requirements were not met", "CCP 1985.3(b),(c),(k); 1985.6(b),(j); 2020.410(d)", a.consumerNotice === "no",
      "Failure to comply is a sufficient basis for a witness to refuse to produce the records.");
    add("burden", "Undue burden or expense on a non-party, or electronic information is not reasonably accessible", "CCP 1987.1(a); 2020.220(e)-(g),(k),(l)", false,
      "A non-party must be protected from undue burden or expense. A person who says electronic information is not reasonably accessible bears the burden of showing that.");
    add("alt", "The information is available from a more convenient or less burdensome source, or is cumulative", "CCP 2020.220(i)", false,
      "Written for electronically stored information. Check how it applies to your subpoena.");
  }
  if (a.stype === "trial_records") {
    add("affidavit", "No supporting affidavit showing good cause was served", "CCP 1985(b)", a.affidavit === "no",
      "A subpoena duces tecum issued before trial must be served with an affidavit showing good cause. A records-only deposition subpoena does not need one (CCP 2020.410(c)).");
  }
  add("privacy", "The subpoena unreasonably violates privacy rights", "CCP 1987.1(a)", ["consumer", "employee", "speech"].includes(a.role), "");
  add("privilege", "The subpoena seeks privileged or work-product material", "CCP 2020.220(j)", false,
    "Privileges themselves come from the Evidence Code and other law, which are not in the downloaded files.");
  add("conf", "The subpoena seeks trade secret or confidential business information (ask for a protective order)", "CCP 1987.1(a)", false,
    "Section 1987.1(a) authorizes protective orders. The substantive trade secret privilege is outside the downloaded files.");
  if (a.role === "speech") {
    add("pii", "The subpoena seeks identifying information in an action arising from free-speech rights", "CCP 1987.1(b)(5); 1987.2(c)", true,
      "Section 1987.2(c) requires a fee award to the moving party in certain cases involving internet service providers. Civil Code 1798.79.8 is not in the files.");
  }
  add("other", "Other ground (describe in your papers)", "", false, "");
  return list;
}

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "role",
    title: "Who are you in relation to the subpoena?",
    intro: "The route depends on your role. Consumers and employees whose records are sought have their own statutory procedures.",
    fields: [
      { k: "role", type: "radio", label: "Your role", req: true, opts: opts(ROLE_TXT) },
      {
        k: "route", type: "radio", label: "How do you want to proceed?", req: true,
        show: (a) => a.role === "consumer" || a.role === "employee",
        opts: (a) => [
          { v: "objection", l: `Serve a written objection (the statutory route for a non-party ${a.role})`, h: "Served on the parties, not filed with the court. No motion fee." },
          {
            v: "motion", l: "File a motion to quash or modify under CCP 1987.1",
            h: a.role === "consumer"
              ? "CCP 1985.3(g) gives the motion route to consumers who are parties. CCP 1987.1(b)(3) also lists consumers. Confirm with an attorney or the clerk that a non-party consumer can use it."
              : "CCP 1985.6(f)(1) allows any employee to bring the motion.",
          },
        ],
      },
      {
        k: "entity", type: "radio", label: "Are you an individual or an organization?", req: true,
        show: (a) => a.role === "witness" || a.role === "other",
        opts: [{ v: "person", l: "An individual" }, { v: "org", l: "An organization (company, agency, etc.)" }],
      },
    ],
  },
  {
    id: "case",
    title: "The case and the court department",
    intro: "This determines which department hears the motion. The wizard asks only what changes your checklist. It does not ask for names or case numbers.",
    fields: [
      {
        k: "parity", type: "radio", label: "Is the case number ODD or EVEN?", req: true,
        hint: "Use the final numeric digit of the case number. Dept. 301 hears odd numbers and Dept. 302 hears even numbers.",
        opts: [{ v: "odd", l: "Odd (ends in 1, 3, 5, 7 or 9)" }, { v: "even", l: "Even (ends in 0, 2, 4, 6 or 8)" }],
      },
      {
        k: "assign", type: "radio", label: "Is the case any of the following?", req: true,
        opts: [
          { v: "general", l: "An ordinary civil case in the Law & Motion / Discovery departments" },
          { v: "single", l: "Assigned to one judge for all purposes, or assigned to a trial department", h: "Discovery matters go to the assigned judge unless that judge decides otherwise (LR 10.0(A)(1))." },
          { v: "probfam", l: "A probate or family law case", h: "Heard by the assigned probate or family law judge (LR 10.0(A)(2))." },
          { v: "ud", l: "An unlawful detainer case (Real Property Court, Dept. 501)" },
          { v: "realprop", l: "A real property case (Real Property Court)" },
          { v: "asbestos", l: "An asbestos or CEQA case (Dept. 304)" },
        ],
      },
    ],
  },
  {
    id: "subpoena",
    title: "The subpoena you received or are challenging",
    intro: "The type of subpoena decides which rules apply, including whether a separate statement is required.",
    fields: [
      { k: "stype", type: "radio", label: "What kind of subpoena is it?", req: true, opts: opts(TYPE_TXT) },
      { k: "issued", type: "date", label: "Date the subpoena was issued (shown on its face, optional)" },
      { k: "served", type: "date", label: "Date it was served (optional)" },
      { k: "comply", type: "date", label: "Date for compliance, production or appearance", req: true, hint: "The production date is the key deadline for everything else." },
    ],
  },
  {
    id: "defects",
    title: "Possible defects in the subpoena",
    intro: "Answer what you can. Your answers become suggested grounds in a later step.",
    skipIf: (a) => a.role !== "witness" && !(a.role === "other" && hasTestimony(a)),
    fields: [
      {
        k: "servedHow", type: "radio", label: "How was the subpoena delivered to you?", req: true, show: (a) => a.role === "witness",
        hint: "Deposition subpoenas and trial subpoenas are served by personal delivery. For a deposition, that means on the person, or on an officer, director, custodian of records or authorized agent of an organization.",
        opts: [
          { v: "personal", l: "Handed to me (or to an officer, custodian or authorized agent)" },
          { v: "other", l: "Mailed, emailed, left with someone else, or other" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        k: "fee", type: "radio", label: "Was a witness fee (and mileage, if you must attend) paid or offered?", req: true, show: (a) => a.role === "witness",
        opts: [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Not sure" }],
      },
      {
        k: "distance", type: "radio", label: "Where is the deposition set to take place?", req: true,
        show: (a) => (a.role === "witness" || a.role === "other") && hasTestimony(a),
        opts: (a) => a.entity === "org"
          ? [
            { v: "near", l: "Within 75 miles of the organization's principal California office" },
            { v: "far", l: "More than 75 miles from that office" },
            { v: "unsure", l: "Not sure" },
          ]
          : [
            { v: "near", l: "Within 75 miles of my residence" },
            { v: "mid", l: "More than 75 miles from my residence, but inside the county where the case is pending and within 150 miles" },
            { v: "far", l: "Farther than that" },
            { v: "unsure", l: "Not sure" },
          ],
      },
      {
        k: "consumerRecords", type: "radio", label: "Do the records concern a specific customer, patient, client or employee?", req: true,
        show: (a) => a.role === "witness" && hasRecords(a) && !isTrialType(a),
        opts: [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Not sure" }],
      },
      {
        k: "consumerNotice", type: "radio", label: "Did the subpoena arrive with a proof of service of notice to that person, or with their written authorization?", req: true,
        show: (a) => a.role === "witness" && a.consumerRecords === "yes",
        opts: [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Not sure" }],
      },
      {
        k: "affidavit", type: "radio", label: "Was a supporting affidavit showing good cause served with the subpoena?", req: true,
        show: (a) => a.stype === "trial_records",
        opts: [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Not sure" }],
      },
    ],
  },
  {
    id: "timing",
    title: "Timing and notice",
    intro: "The motion needs at least 16 court days' notice unless the court shortens time. Dates are computed with San Francisco court holidays through 2027.",
    skipIf: skipIfObjection,
    fields: [
      { k: "planServe", type: "date", label: "Date you plan to file and serve the motion", req: true, def: todayISO },
      {
        k: "servMethod", type: "radio", label: "How will you serve the motion papers?", req: true,
        hint: "Non-parties are excluded from SF's e-filing and e-service requirements, so paper filing and paper service are fine. Electronic service is optional and adds court days.",
        opts: [
          { v: "personal", l: "Personal delivery or hand delivery" },
          { v: "eservice", l: "Electronic service", h: "Adds 2 court days (CCP 1010.6)." },
          { v: "mail_ca", l: "Mail, within California", h: "Adds 5 calendar days (CCP 1005(b))." },
          { v: "overnight", l: "Overnight delivery, express mail or fax", h: "Adds 2 calendar days (CCP 1005(b))." },
          { v: "mail_us", l: "Mail, outside California but within the U.S.", h: "Adds 10 calendar days (CCP 1005(b))." },
        ],
      },
      { k: "hearingPref", type: "date", label: "Hearing date you want to select (optional)", hint: "Leave blank to use the earliest date available under regular notice." },
    ],
  },
  {
    id: "grounds",
    title: "Grounds for quashing or limiting the subpoena",
    intro: "Select the grounds you intend to raise. Grounds marked Suggested come from your earlier answers.",
    fields: [
      { k: "grounds", type: "check", label: "Grounds", opts: (a) => groundsCatalog(a).map((g) => ({ v: g.id, l: g.text, h: [g.cite, g.note].filter(Boolean).join(". "), tag: g.suggested ? "Suggested" : "" })) },
    ],
  },
  {
    id: "confer",
    title: "Meet and confer",
    intro: "A meet-and-confer declaration is required for discovery motions (CCP 2016.040) and for a protective-order motion (CCP 2025.420(a)).",
    skipIf: skipIfObjection,
    fields: [
      {
        k: "conferMethod", type: "radio", label: "How did you (or will you) confer?", req: true,
        hint: "CCP 2016.040(a) speaks of attempts in person, by telephone or by videoconference.",
        opts: [{ v: "live", l: "In person, by phone or by video" }, { v: "written", l: "By letter or email only" }],
      },
      {
        k: "extension", type: "radio", label: "Have they agreed in writing to extend or suspend the compliance date?", req: true,
        opts: [{ v: "yes", l: "Yes, in writing" }, { v: "pending", l: "I have asked and am waiting" }, { v: "no", l: "No" }],
      },
    ],
  },
  {
    id: "relief",
    title: "Relief, confidentiality and hearing logistics",
    intro: "These answers shape the documents, sealing steps and hearing steps in your checklist.",
    skipIf: skipIfObjection,
    fields: [
      {
        k: "relief", type: "check", label: "What will you ask the court to do?",
        opts: [
          { v: "quash", l: "Quash the subpoena entirely" },
          { v: "modify", l: "Modify or narrow the subpoena" },
          { v: "protect", l: "Set conditions or a protective order" },
          { v: "costs", l: "Award expenses and attorney's fees (CCP 1987.2)" },
        ],
      },
      {
        k: "confid", type: "radio", label: "Will you need to put confidential material in front of the court?", req: true,
        hint: "For example, records you claim are privileged, private or confidential, so the judge can rule on your objection.",
        opts: [{ v: "no", l: "No" }, { v: "part", l: "Yes, only portions of documents" }, { v: "whole", l: "Yes, entire documents" }],
      },
      {
        k: "appear", type: "radio", label: "How do you plan to appear at the hearing?", req: true,
        opts: [{ v: "zoom", l: "Remotely by Zoom" }, { v: "person", l: "In person" }, { v: "undecided", l: "Undecided" }],
      },
      {
        k: "reporter", type: "radio", label: "Do you want a court reporter at the hearing?", req: true,
        hint: "The Discovery Department does not provide an official reporter.",
        opts: [{ v: "own", l: "Yes, I will hire a certified shorthand reporter" }, { v: "none", l: "No" }, { v: "undecided", l: "Undecided" }],
      },
    ],
  },
];

/** Shown on the review step. */
export function summary(a) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Role", ROLE_TXT[a.role]);
  if (a.route) add("Route", a.route === "objection" ? "Written objection" : "Motion under CCP 1987.1");
  add("Department", a.parity ? `Dept. ${deptOf(a)} (${a.parity} case number)` : "");
  add("Subpoena", TYPE_TXT[a.stype]);
  add("Issued / served / compliance", [a.issued && fmtISO(a.issued), a.served && fmtISO(a.served), a.comply && fmtISO(a.comply)].filter(Boolean).join("  |  "));
  add("Plan to serve motion", a.planServe ? `${fmtISO(a.planServe)} by ${METHOD_TXT[a.servMethod] || ""}` : "");
  const gs = (a.grounds || []).map((g) => (groundsCatalog(a).find((x) => x.id === g) || {}).text).filter(Boolean);
  add("Grounds", gs.length ? gs.join("; ") : "None selected");
  return rows;
}

/* ------------------------------------------------------------------ dates */
function compute(a) {
  const r = { warnings: [] };
  const serve = a.planServe ? parse(a.planServe) : new Date();
  const m = a.servMethod || "personal";
  const calExt = { mail_ca: 5, overnight: 2, mail_us: 10 }[m] || 0;
  const courtExt = m === "eservice" ? 2 : 0;
  r.notice = 16 + courtExt;
  r.calExt = calExt;
  r.earliest = addCourtDays(addDays(serve, calExt), r.notice);
  r.hearing = r.earliest;
  if (a.hearingPref) {
    const hp = parse(a.hearingPref);
    if (!isCourtDay(hp)) r.warnings.push(`The hearing date you chose (${fmt(hp)}) is not a court day. Discovery motions are heard Monday to Friday, excluding court holidays.`);
    else if (hp < r.earliest) r.warnings.push(`Your chosen hearing date is earlier than the earliest date allowed under regular notice (${fmt(r.earliest)}). Use that date or later, or get an order shortening time.`);
    else r.hearing = hp;
  }
  r.opp = addCourtDays(r.hearing, -9);
  r.reply = addCourtDays(r.hearing, -5);
  r.pos = addCourtDays(r.hearing, -5);
  r.tentative = prevCourtDay(r.hearing);
  if (a.comply) {
    const c = parse(a.comply);
    r.comply = c;
    r.lastHearing = prevCourtDay(c);
    r.lastServe = addDays(addCourtDays(r.lastHearing, -r.notice), -calExt);
    r.tooLate = r.hearing >= c;
    r.serveTooLate = r.lastServe < new Date(new Date().setHours(0, 0, 0, 0));
    r.fiveDay = addDays(c, -5);
  }
  if (r.hearing > parse(HOLIDAY_DATA_THROUGH)) r.warnings.push("The dates run past the holiday data in this tool (through 2027). Recheck them against the court's holiday schedule.");
  return r;
}

function deptText(a, dept) {
  const map = {
    general: `Discovery Department, Dept. ${dept}. Discovery calendar at 9:00 a.m., Monday to Friday.`,
    single: `The assigned judge or trial department, unless the assigned judge sends it to Dept. ${dept} (Discovery).`,
    probfam: "The assigned probate or family law judge.",
    ud: "Real Property Court (Dept. 501). Discovery motions in unlawful detainer cases are calendared there at 9:30 a.m.",
    realprop: `Law and Motion Department (Dept. ${dept}) at 9:00 a.m. for real property discovery motions. Ex parte discovery applications go to the Real Property Court.`,
    asbestos: "Dept. 304 (asbestos and CEQA).",
  };
  return (map[a.assign] || "") + (isTrialType(a) ? " For a trial subpoena, confirm with the trial department." : "");
}

/* ------------------------------------------------------------------ checklist */
export function build(a) {
  const R = routeOf(a);
  const D = compute(a);
  const dept = deptOf(a);
  const grounds = (a.grounds || []).map((g) => groundsCatalog(a).find((x) => x.id === g)).filter(Boolean);
  const secs = [];
  const flags = [];
  const verify = [];
  const P = (sid, title, blurb) => {
    const s = { id: sid, title, blurb: blurb || "", items: [] };
    secs.push(s);
    return s;
  };
  const I = (s, key, text, cite, note) => s.items.push({ key: `${s.id}.${key}`, text, cite: cite || "", note: note || "" });

  /* ---- flags ---- */
  if (R === "motion" || R === "protective") {
    if (D.comply && D.tooLate) {
      flags.push({ t: "bad", b: "Timing problem.", m: `With regular notice the earliest hearing is ${fmt(D.earliest)}, which is on or after the compliance date of ${fmt(D.comply)}. To get a ruling first you need a written extension from the subpoenaing party or an order shortening time. See "If the hearing cannot happen before the compliance date" below.` });
    } else if (D.comply && D.serveTooLate) {
      flags.push({ t: "warn", b: "You are past the latest service date", m: `for a regular-notice hearing before compliance (${fmt(D.lastServe)}). Plan for an extension or an order shortening time.` });
    } else if (D.comply) {
      flags.push({ t: "ok", b: "Timing works on paper.", m: `Serve by ${fmt(D.lastServe)} at the latest for a hearing before the compliance date. Serving on ${fmtISO(a.planServe)} gives a hearing on or after ${fmt(D.earliest)}.` });
    }
    if (a.extension !== "yes") {
      flags.push({ t: "warn", b: "No written extension yet.", m: "Nothing in the downloaded files says that filing a motion, by itself, excuses a non-party witness from complying on the compliance date. Do not ignore the subpoena. Deponents who disobey a deposition subpoena can be held in contempt (CCP 2020.240)." });
    }
  }
  if (a.conferMethod === "written") flags.push({ t: "warn", b: "Meet and confer.", m: "CCP 2016.040(a) refers to attempts in person, by telephone or by video. Emails alone may not satisfy it. Add a live conversation before filing." });
  D.warnings.forEach((w) => flags.push({ t: "warn", b: "", m: w }));
  if (a.stype === "unsure") flags.push({ t: "info", b: "", m: "You are unsure of the subpoena type. Check its title and face page: deposition subpoenas mention a deposition and a deposition officer, and trial subpoenas name the court and trial or hearing date. Rules below assume a deposition subpoena unless you chose otherwise." });
  if (a.role === "other" && isTrialType(a)) flags.push({ t: "bad", b: "The protective-order route in CCP 2025.420 applies to depositions.", m: "Your subpoena is for a trial or hearing, so check whether you have standing under CCP 1987.1(b) before proceeding." });

  /* ---- Phase 0 ---- */
  const p0 = P("p0", "Phase 0. Today", "Protect your position first.");
  I(p0, "cal", `Calendar the compliance date (${D.comply ? fmt(D.comply) : "date from your subpoena"}) and work backward from it.`);
  I(p0, "keep", "Preserve everything the subpoena covers. Do not delete or alter anything while you challenge it.", "CCP 2020.220(m)(2)", "Sanction protection for routine, good-faith loss of electronic data does not remove the duty to preserve.");
  I(p0, "copy", "Keep the original subpoena, the envelope or delivery record, and proof of how and when it was served.");
  if (a.role === "witness") I(p0, "noignore", "Do not simply ignore the subpoena. Until the court rules or the issuing party agrees in writing to defer, treat the compliance date as binding.", "CCP 2020.240");

  /* ---- Route-specific ---- */
  if (R === "objection") {
    const who = a.role === "consumer" ? "consumer" : "employee";
    const cite = a.role === "consumer" ? "CCP 1985.3(g)" : "CCP 1985.6(f)(2)";
    const p1 = P("obj", "Phase 1. Serve your written objection", `A non-party ${who} objects in writing. The objection is served, not filed.`);
    I(p1, "grounds", "Write an objection that cites the specific grounds why production of your records should be prohibited.", cite);
    if (grounds.length) I(p1, "yourgrounds", `Grounds you selected: ${grounds.map((g) => g.text).join("; ")}.`);
    I(p1, "serve", "Serve the objection on all three: the subpoenaing party, the witness (the business or employer holding the records), and the deposition officer named on the subpoena.", cite);
    I(p1, "when", `Serve it before the production date${D.comply ? ` (${fmt(D.comply)})` : ""}. Serve well ahead, by a method that proves delivery.`, cite);
    I(p1, "effect", "Know the effect: after the witness or deposition officer receives your objection, they are not required to produce the records unless the court orders it or all affected persons agree.", cite);
    I(p1, "proof", "Keep proof of service of the objection.");
    I(p1, "enforce", "If the requesting party moves to enforce the subpoena, they must do so within 20 days of service of your objection, with a declaration showing a good-faith attempt at informal resolution. Calendar that 20-day window, then prepare an opposition.", a.role === "consumer" ? "CCP 1985.3(g)" : "CCP 1985.6(f)(4)");
    I(p1, "opp", "If they file the enforcement motion, your opposition is due at least 9 court days before the hearing.", "CCP 1005(b)");
  }
  if (R === "protective") {
    const p1 = P("prot", "Phase 1. Choose the protective-order motion", 'You are an "other affected" person, so the route is a motion for a protective order.');
    I(p1, "who", 'Confirm you fit "any other affected natural person or organization".', "CCP 2025.420(a)");
    I(p1, "prompt", "File the motion promptly. It may be brought before, during or after the deposition.", "CCP 2025.420(a)");
    I(p1, "mc", "Attach the meet-and-confer declaration.", "CCP 2025.420(a); 2016.040");
    I(p1, "good", "Show good cause. Available orders include: no deposition, a different time or place, specified terms, limits on subject matter, and that listed documents not be produced.", "CCP 2025.420(b)");
  }

  /* ---- Meet and confer ---- */
  if (R !== "objection") {
    const pm = P("mc", "Phase 1. Meet and confer, then get it in writing");
    I(pm, "contact", "Contact the subpoenaing party by phone or video (or in person) and explain each objection and what you will accept.", "CCP 2016.040(a)");
    I(pm, "confirm", "Send a same-day confirming letter or email summarizing what was said and any agreement.");
    I(pm, "ext", "Ask for a written stipulation extending the compliance date until the court rules.", "", a.extension === "yes" ? "You already have one. Keep it." : "");
    I(pm, "csr", "Also discuss whether a certified shorthand reporter will be retained for the hearing. Your declaration must say whether you conferred on that.", "CCP 2016.040(b)");
    I(pm, "log", "Keep a log: date, time, method, who took part, what each side said.");
  }

  /* ---- Documents ---- */
  if (R !== "objection") {
    const nm = a.role === "witness" && a.entity === "org" ? "[ORGANIZATION]" : "[NAME]";
    const ttl = R === "protective"
      ? `NON-PARTY ${nm}'S NOTICE OF MOTION AND MOTION FOR PROTECTIVE ORDER`
      : `NON-PARTY ${nm}'S NOTICE OF MOTION AND MOTION TO QUASH${a.role === "witness" ? "" : " OR MODIFY"} ${isDep(a) ? "DEPOSITION SUBPOENA" : "SUBPOENA"}${a.stype === "dep_records" ? " FOR BUSINESS RECORDS" : ""}`;
    const pd = P("docs", "Phase 2. Prepare the papers", `Suggested title: ${ttl}`);
    const rp = a.assign === "ud" || a.assign === "realprop";
    I(pd, "notice", `Notice of motion and motion. State the hearing date and department (${dept}), the relief you request, and the grounds.${rp ? ' In the Real Property Court, type the word "discovery" on the title page of all discovery papers.' : ""}`, rp ? "CCP 1987.1(a); LR 2.7; LR 8.10(A)(4)" : "CCP 1987.1(a); LR 2.7");
    I(pd, "format", "Follow the court's format rules for the papers.", "CRC 2.100-2.119; 3.1110-3.1116; LR 2.7(A)");
    I(pd, "memo", "Memorandum of points and authorities: 15 pages maximum (a reply is limited to 10). A memorandum over 10 pages needs a table of contents and a table of authorities. To file a longer one, apply to the court in advance.", "CRC 3.1113(d)-(e)");
    I(pd, "decl", "Declaration stating the facts: who you are, what was served, when and how, the burden or privacy or other harm, and your dates for the compliance deadline.", "CCP 1987.1(a)");
    I(pd, "mcdecl", "Meet-and-confer declaration: facts showing a reasonable, good-faith attempt to informally resolve each issue in the motion. Also state whether you conferred about retaining a certified shorthand reporter for the hearing.", "CCP 2016.040(a),(b)");
    if (["dep_records", "dep_both", "unsure"].includes(a.stype)) {
      I(pd, "sep", 'Separate statement. A motion "to compel or to quash the production of documents or tangible things at a deposition" requires one. It must be complete on its own: for each request, the full request and your position. It cannot incorporate other papers by reference.', "CRC 3.1345(a)(5),(c)", "Exceptions are in CRC 3.1345(b). Read them.");
    } else if (isTrialType(a)) {
      I(pd, "nosep", "A separate statement is required for deposition production motions. Your subpoena is a trial subpoena, so CRC 3.1345(a)(5) does not appear to require one. Confirm with the department.", "CRC 3.1345(a)(5)");
    }
    I(pd, "exh", "Exhibits: a copy of the subpoena, the proof of service of the subpoena, and your meet-and-confer correspondence.");
    I(pd, "order", "Proposed order (quashing, modifying, or setting conditions).", "CCP 1987.1(a)");
    if ((a.relief || []).includes("costs")) I(pd, "costs", "If you request expenses and fees, say so in the notice and support it with a declaration on fees. The court can award them if the subpoena was oppressive or the motion was opposed without substantial justification.", "CCP 1987.2(a)");
    I(pd, "pos", `Proof of service of the motion papers. File it at least 5 court days before the hearing (by ${fmt(D.pos)}).`, "CRC 3.1300(c)");
    if (a.role === "consumer" || a.role === "employee") I(pd, "notice5", `Give notice of the motion to the witness and the deposition officer at least 5 days before production${D.fiveDay ? ` (by ${fmt(D.fiveDay)})` : ""}.`, a.role === "consumer" ? "CCP 1985.3(g)" : "CCP 1985.6(f)(1)");
  }

  /* ---- Sealing ---- */
  if (R !== "objection" && a.confid && a.confid !== "no") {
    const ps = P("seal", "Phase 3. Handling confidential material", "Your papers must not reveal the confidential material publicly.");
    I(ps, "nodis", "Do not describe the protected content in the public papers.", "CRC 2.551(c)");
    I(ps, "exc", "Discovery motion papers have their own track. The sealed-records rules do not apply to discovery motions and records filed with them, so a separate motion to seal is not required for them. The rules do apply to discovery material used at trial or submitted to decide something other than a discovery dispute.", "CRC 2.550(a)(3); SF E-Filing Special Instructions");
    I(ps, "cap", `Mark the first caption page "DISCOVERY" and "TO BE FILED UNDER SEAL" in caps, bold and underlined.${a.confid === "part" ? ' Because only part is confidential, file a public version marked "REDACTED" and a complete version marked "UNREDACTED".' : ""}`, "SF E-Filing Special Instructions, Sealed Discovery Documents");
    I(ps, "redact", "Truly remove the sensitive text and its metadata. Highlighting it black is not sufficient.", "SF E-Filing Special Instructions");
    I(ps, "courtesy", "Submit un-redacted courtesy copies to the department. Highlight the confidential parts so the judge can tell them apart.", "SF E-Filing Special Instructions");
    I(ps, "nonparty", "The instructions are written for e-filers. As a non-party filing on paper, ask the clerk in Room 103 exactly how to submit sealed or lodged material.");
    verify.push("Sealing steps for a non-party filing conventionally are not addressed in the downloaded files.");
  }

  /* ---- Fees ---- */
  const pf = P("fees", "Phase 4. Fees", R === "objection" ? "Nothing in the files sets a fee for serving a written objection. It is served, not filed." : "Bring payment when you file.");
  if (R !== "objection") {
    I(pf, "motion", "Motion fee: $60. A discovery motion is a motion requiring a hearing subsequent to the first paper.", "GC 70617(a)(4); Statewide Civil Fee Schedule, items 46 and 49");
    I(pf, "first", "You should not owe a first-paper fee. The $435 and similar first-paper fees are charged to parties who appear. The $60 exception for a party's first paper does not help a non-party, so the $60 motion fee applies.", "GC 70617(a),(b)(1); Fee Schedule items 5 and 46", "This is a reading of the statute. Confirm with the clerk.");
    I(pf, "separate", "Each separate motion or paper is charged separately, even if heard together.", "GC 70617(f)");
    I(pf, "cont", "If you later ask to continue the hearing: $20.", "GC 70617(c)(1); Fee Schedule item 57");
    I(pf, "ex", "If you need an ex parte application requiring notice to the other parties: $60.", "GC 70617(a)(8); Fee Schedule item 51");
    I(pf, "pay", "Ask the clerk which payment methods are accepted.");
    verify.push("Fee waiver eligibility and accepted payment methods are not in the downloaded files.");
  } else {
    I(pf, "none", "No filing fee is expected for a served objection. If the requesting party later files an enforcement motion, that is their fee.");
  }

  /* ---- Filing and service ---- */
  if (R !== "objection") {
    const pfs = P("file", "Phase 5. Choose the date, file and serve");
    I(pfs, "date", `Pick a hearing date after conferring with the other side. No reservation is needed. Write the date on the motion. Earliest available under regular notice: ${fmt(D.earliest)}.`, "LR 10.0(C); LR 8.2(A)(2)(a)");
    I(pfs, "holiday", "Do not notice the hearing on a court holiday. The court will not continue it to the next day on its own.", "LR 8.2(C)(3)");
    if (a.assign === "single") I(pfs, "single", "The case is assigned to one judge or a trial department. Confirm with that department where and when to notice a discovery motion. The assigned judge may hear it instead of Dept. 301 or 302.", "LR 10.0(A)(1)");
    if (a.assign === "probfam") I(pfs, "pf", "Notice the motion for the assigned probate or family law judge.", "LR 10.0(A)(2)");
    if (a.assign === "asbestos") I(pfs, "asb", "Asbestos and CEQA cases are heard in Dept. 304. Check the department's page for procedures.", "SF Law & Motion & Discovery page, table of departments");
    I(pfs, "paper", "File the papers on paper at the clerk's office, Room 103. Non-parties are excluded from the e-filing requirement (they may e-file voluntarily if they choose).", "LR 2.11(D)(2); LR 8.2(A)(2)(b)");
    I(pfs, "serve", "Serve the subpoenaing party and every other party who has appeared. As a non-party you are excluded from the e-service requirement, so paper service is fine. If you serve electronically, add 2 court days.", "CCP 1005(b); CCP 1010.6; LR 2.11(D)(2)");
    I(pfs, "days", `Serve and file at least ${D.notice} court days before the hearing${D.calExt ? `, plus ${D.calExt} calendar days for your service method` : ""}. That places the earliest hearing on ${fmt(D.earliest)}.`, "CCP 1005(b); CCP 1010.6");
    I(pfs, "tab", `Deliver tabbed and bound paper courtesy copies to Dept. ${dept}. For papers filed on paper, lodge them when you file. If you file fewer than 7 court days before the hearing, lodge them the same day.`, "LR 2.7(B)");
    I(pfs, "pos", "File the proof of service at least 5 court days before the hearing.", "CRC 3.1300(c)");
  }

  /* ---- Contingency ---- */
  if (R !== "objection" && (D.tooLate || D.serveTooLate || a.extension !== "yes")) {
    const pc = P("late", "If the hearing cannot happen before the compliance date", "Use this when regular notice will not give you a ruling in time.");
    I(pc, "stip", "Get a written stipulation from the subpoenaing party to defer compliance until the court rules. This is the cleanest fix.");
    I(pc, "ost", "Ask the court to shorten time. Support the request with a declaration showing good cause.", "CRC 3.1300(b); CCP 1005(b)");
    I(pc, "exp", 'To do so by ex parte application: ex parte hearings are held at 11:00 a.m., Monday to Friday, by CourtCall only (1-888-88-COURT), with no in-person appearances. Endorsed-filed copies of the moving papers must reach the department no later than 2 hours before the hearing. The court\'s rule requires scheduling with the clerk at least 24 hours ahead for TROs, alternative writs, receivers "or similar matter". The rule says to call 415-551-3823, while the department webpage says to email calendar301@sftc.org or calendar302@sftc.org. Confirm which applies to your application.', "LR 9.0(A); SF Law & Motion & Discovery page, Ex Parte Hearings");
    I(pc, "exfee", "An ex parte application requiring notice to the other parties carries a $60 fee.", "GC 70617(a)(8)");
    if (a.role === "employee" || a.role === "consumer") I(pc, "5day", "If you are the consumer or employee whose records are sought, a motion brought by you, with notice to the witness and deposition officer at least 5 days before production, means the witness need not produce except by court order or agreement.", "CCP 1985.3(g); 1985.6(f)(3)");
    else I(pc, "nostay", "No provision in the downloaded files automatically stays compliance when a non-party witness files a motion to quash. CCP 2025.410(c), which stays a deposition, is given to a party moving to quash a deposition notice.", "CCP 2025.410(c)");
  }

  /* ---- Before the hearing ---- */
  if (R !== "objection") {
    const ph = P("pre", "Phase 6. Between filing and the hearing");
    I(ph, "opp", `Watch for the opposition. It is due at least 9 court days before the hearing (${fmt(D.opp)}). The reply is due at least 5 court days before (${fmt(D.reply)}).`, "CCP 1005(b)");
    I(ph, "tent", `Check the tentative ruling. It is generally posted by 3:00 p.m. the court day before (${fmt(D.tentative)}). Look on the court's tentative rulings page or call (415) 551-4000.`, "LR 8.3(B),(F)");
    I(ph, "contest", `If you disagree, email contestdept${dept}tr@sftc.org by 4:00 p.m. the day before with a copy to all parties, saying without argument which parts you contest. If you skip this, you cannot argue.`, "LR 8.3(D)");
    I(ph, "submit", "If you do not appear, you are deemed to submit to the tentative ruling.", "LR 8.3(C)");
    I(ph, "appear", `Appear ${a.appear === "person" ? "in person" : a.appear === "zoom" ? "remotely by Zoom" : "in person or by Zoom (decide early)"}. Discovery hearings are at 9:00 a.m. in Dept. ${dept} and remote appearances are permitted.`, "LR 8.2(A)(3); SF Law & Motion & Discovery page");
    I(ph, "rep", a.reporter === "own" ? "Hire a California-certified shorthand reporter who can report a Zoom hearing. The court does not provide one." : "Decide about a court reporter. The Discovery Department does not provide one.", "SF Law & Motion & Discovery page");
    I(ph, "contnce", `To continue or take the motion off calendar, email calendar${dept}@sftc.org with a copy to all parties. A continuance request must state that all affected parties agree to the new date.`, "LR 8.2(C)(1)");
  }

  /* ---- After ---- */
  if (R !== "objection") {
    const pa = P("post", "Phase 7. After the ruling");
    I(pa, "order", "If you prevail, prepare a proposed order that repeats the substantive part of the tentative ruling verbatim and bring it to the hearing, even if the motion is unopposed.", "LR 8.3(G)");
    I(pa, "serveorder", "File the signed order and serve a filed-endorsed copy on all parties.", "LR 8.4(B)");
    I(pa, "comply", "Tell the witness or deposition officer the outcome in writing, and calendar any new compliance date the order sets.");
    I(pa, "costs", "Be aware the court can award expenses and fees against either side if a motion is made or opposed in bad faith or without substantial justification, or the subpoena was oppressive.", "CCP 1987.2(a)");
  }

  verify.push("Case law and privilege law (trade secret, attorney-client, privacy balancing) are not in the downloaded files.");
  verify.push("Whether a non-party's pending motion suspends the duty to comply, other than for consumers and employees under CCP 1985.3(g) and 1985.6(f).");
  verify.push("CRC 3.1112 (motion format) and the CRC 3.1345(b) exceptions were not read in full.");
  verify.push("Court-day counts use SF court holidays from the court's website through 2027.");

  let n = 0;
  secs.forEach((x) => {
    if (/^Phase \d+\./.test(x.title)) {
      x.title = x.title.replace(/^Phase \d+\./, `Phase ${n}.`);
      n++;
    }
  });

  const routeName = {
    motion: "Motion to quash or modify the subpoena (CCP 1987.1)",
    objection: `Written objection (${a.role === "consumer" ? "CCP 1985.3(g)" : "CCP 1985.6(f)(2)"})`,
    protective: "Motion for a protective order (CCP 2025.420)",
  }[R];

  const rows = [["Route", routeName], ["Subpoena", TYPE_TXT[a.stype] || ""]];
  if (R !== "objection") rows.push(["Where it is heard", deptText(a, dept)]);
  if (D.comply) rows.push(["Compliance date", fmt(D.comply)]);
  if (R !== "objection") {
    rows.push(["Earliest hearing (regular notice)", fmt(D.earliest), `${D.notice} court days${D.calExt ? ` plus ${D.calExt} calendar days` : ""} from ${fmtISO(a.planServe)}`]);
    if (D.lastServe) rows.push(["Latest service date for a hearing before compliance", fmt(D.lastServe)]);
    rows.push(["Opposition and reply due", `${fmt(D.opp)} and ${fmt(D.reply)}`]);
    rows.push(["Fee", "$60 motion fee"]);
  } else {
    rows.push(["Fee", "None expected for a served objection"]);
  }

  return { title: "Your checklist", flags, rows, sections: secs, verify };
}
